/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-danger */
import React, {FC, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import icon_goal from 'assets/svg/match_timeline_icon_goal.svg';
import icon_var from 'assets/svg/match_timeline_icon_var.svg';
import icon_cardy from 'assets/svg/match_timeline_icon_card-y.svg';
import icon_cardr from 'assets/svg/match_timeline_icon_card-r.svg';
import icon_cardyr from 'assets/svg/match_timeline_icon_card-yr.svg';

import {loadScriptsInSequence} from 'utils/domUtils';
import {
  Team,
  TimelineUpdate,
  TimelineUpdateType,
  ScoreContent,
  CardContent,
  StatusContent,
  EditorSignedContent,
  EditorPhotoContent,
  EditorEmbedContent,
  TeamInfo,
  Sport,
} from '../../../utils/types';

import './styles.scss';

interface Props {
  update: TimelineUpdate;
}
declare global {
  interface Window {
    cnx?: any; // Declare cnx as an optional property
  }
}
/**
 * CONTENT TYPE & STRUCTURE ASSERTIONS
 */
// TODO: flesh out assertion functions

const isCardContent = (c: any): c is CardContent => c && typeof c !== 'string' && 'player' in c;

const isScoreContent = (c: any): c is ScoreContent =>
  c &&
  typeof c !== 'string' &&
  'score' in c &&
  c.score &&
  'home' in c.score &&
  'away' in c.score &&
  'scorer' in c &&
  'type' in c;

const isStatusContent = (c: any): c is StatusContent =>
  c && typeof c !== 'string' && 'statusType' in c;

const hasScoreInStatus = (c: StatusContent): c is Required<StatusContent> =>
  !!(
    'score' in c &&
    c.score &&
    'home' in c.score &&
    c.score.home !== null &&
    'away' in c.score &&
    c.score.away !== null
  );

/**
 * SIMPLE UPDATE
 */

interface SimpleProps extends Props {
  sport?: Sport;
  teams?: {home: TeamInfo; away: TeamInfo};
}

export const TimelineUpdateSimple: FC<SimpleProps> = ({
  update: {type, teamType, minute, stoppageTime, title, content, method, comment},
  sport,
  teams,
}: SimpleProps) => {
  const formattedTitle = useMemo(() => {
    if (teams && teamType && teamType !== Team.UNDEFINED)
      return `${title}, ${teams[teamType].name}`;
    return title;
  }, [title, teams, teamType]);

  const formattedTime = useMemo(() => {
    if (sport === Sport.TENNIS || sport === Sport.VOLLEYBALL) {
      if (type === TimelineUpdateType.PERIOD_SCORE) return '';
      if (teams && teamType && teamType !== Team.UNDEFINED) return teams[teamType].shortName;
    }
    return minute ? `${minute}${stoppageTime ? `+${stoppageTime}` : ''}'` : '';
  }, [sport, type, teams, teamType, minute, stoppageTime]);

  const isBoldTitle = useMemo(
    () =>
      [
        TimelineUpdateType.YELLOW_CARD,
        TimelineUpdateType.RED_CARD,
        TimelineUpdateType.YELLOW_RED_CARD,
        TimelineUpdateType.SHOT_ON_TARGET,
        TimelineUpdateType.SHOT_OFF_TARGET,
        TimelineUpdateType.PENALTY_AWARDED,
        TimelineUpdateType.PENALTY_MISSED,
        TimelineUpdateType.INJURY,
        TimelineUpdateType.INJURY_RETURN,
        TimelineUpdateType.SUBSTITUTION,
        TimelineUpdateType.SCORE_CHANGE,
        TimelineUpdateType.PERIOD_SCORE,
        TimelineUpdateType.VIDEO_ASSISTANT_REFEREE_OVER,
      ].includes(type),
    [type]
  );

  const isHighlight = useMemo(
    () =>
      [
        TimelineUpdateType.RED_CARD,
        TimelineUpdateType.YELLOW_CARD,
        TimelineUpdateType.YELLOW_RED_CARD,
        TimelineUpdateType.PENALTY_AWARDED,
        TimelineUpdateType.PENALTY_MISSED,
        TimelineUpdateType.INJURY,
        TimelineUpdateType.SUBSTITUTION,
        TimelineUpdateType.VIDEO_ASSISTANT_REFEREE_OVER,
        TimelineUpdateType.PERIOD_SCORE,
      ].includes(type),
    [type]
  );

  const scorePanel = useMemo(() => {
    if (
      [
        TimelineUpdateType.SCORE_CHANGE,
        TimelineUpdateType.POINT,
        TimelineUpdateType.PERIOD_SCORE,
      ].includes(type) &&
      (isScoreContent(content) || (isStatusContent(content) && hasScoreInStatus(content)))
    ) {
      let scoreHome = `${content.score.home}`;
      let scoreAway = `${content.score.away}`;

      if (sport === Sport.TENNIS && content.score.home > 40) scoreHome = 'AD';
      if (sport === Sport.TENNIS && content.score.away > 40) scoreAway = 'AD';

      return (
        <div className={`TimelineUpdateSimple__score MiniScore MiniScore--${teamType}`}>
          <span>{scoreHome}</span>:<span>{scoreAway}</span>
        </div>
      );
    }
    return null;
  }, [type, content, sport, teamType]);

  const playersAnnotation = useMemo(() => {
    let icon;
    switch (type) {
      case TimelineUpdateType.YELLOW_CARD:
        icon = <img className="TimelineUpdateSimple__icon" src={icon_cardy} alt="Κίτρινη κάρτα" />;
        break;
      case TimelineUpdateType.RED_CARD:
        icon = <img className="TimelineUpdateSimple__icon" src={icon_cardr} alt="Κόκκινη κάρτα" />;
        break;
      case TimelineUpdateType.YELLOW_RED_CARD:
        icon = (
          <img className="TimelineUpdateSimple__icon" src={icon_cardyr} alt="Διπλή κίτρινη κάρτα" />
        );
        break;
      case TimelineUpdateType.VIDEO_ASSISTANT_REFEREE_OVER:
        icon = <img className="TimelineUpdateSimple__icon" src={icon_var} alt="VAR" />;
        break;
      default:
        icon = null;
        break;
    }

    if (
      type === TimelineUpdateType.SUBSTITUTION &&
      content &&
      typeof content !== 'string' &&
      'players' in content &&
      content.players
    ) {
      return (
        <>
          <p className="TimelineUpdateSimple__player TimelineUpdateSimple__player--out">
            {content.players.out}
          </p>
          <p className="TimelineUpdateSimple__player TimelineUpdateSimple__player--in">
            {content.players.in}
          </p>
        </>
      );
    }
    // TODO: optimize this below
    if (
      type === TimelineUpdateType.SCORE_CHANGE &&
      isScoreContent(content) &&
      'scorer' in content &&
      content.scorer &&
      sport !== Sport.HANDBALL
    ) {
      return (
        <p className="TimelineUpdateSimple__text">
          {icon}
          {content.scorer}
        </p>
      );
    }
    if (typeof content === 'object' && 'player' in content && content.player) {
      return (
        <p className="TimelineUpdateSimple__text">
          {icon}
          {content.player}
        </p>
      );
    }
    if (content && typeof content === 'string') {
      return (
        <p className="TimelineUpdateSimple__text">
          {icon}
          {content}
        </p>
      );
    }

    return null;
  }, [type, content]);

  const formattedComment = useMemo(
    () =>
      comment && (
        <p
          className="TimelineUpdateSimple__comment Comment"
          dangerouslySetInnerHTML={{__html: comment}}
        />
      ),
    [comment]
  );

  return (
    <article
      className={`TimelineUpdateSimple TimelineUpdateSimple--${
        sport === Sport.TENNIS ? sport : teamType
      } ${isHighlight ? 'TimelineUpdateSimple--highlight' : ''}`}
    >
      <span className="TimelineUpdateSimple__time">{formattedTime}</span>
      <div className="TimelineUpdateSimple__bullet" />
      <div className="TimelineUpdateSimple__content">
        {scorePanel}
        <h1
          className={`TimelineUpdateSimple__title ${
            isBoldTitle ? 'TimelineUpdateSimple__title--bold' : ''
          }`}
        >
          {formattedTitle}
        </h1>
        {playersAnnotation}
      </div>
      {formattedComment}
    </article>
  );
};

/**
 * GOAL UPDATE
 */

interface GoalProps extends Props {
  teams: {home: TeamInfo; away: TeamInfo};
}

export const TimelineUpdateGoal: FC<GoalProps> = ({
  update: {teamType, minute, stoppageTime, teamName, title, content, comment},
  teams,
}: GoalProps) => {
  const isOwn = useMemo(() => isScoreContent(content) && content.type === 'own_goal', [content]);

  const scorePanel = useMemo(() => {
    const homeScore = isScoreContent(content) && content.score.home;
    const awayScore = isScoreContent(content) && content.score.away;

    return isScoreContent(content) ? (
      <div className="TimelineUpdateGoal__scorePanel">
        <span
          className={`TimelineUpdateGoal__score ${
            teamType === Team.HOME ? 'TimelineUpdateGoal__score--bold' : ''
          }`}
        >
          {homeScore}
        </span>
        <span className="TimelineUpdateGoal__scoreSeparator">:</span>
        <span
          className={`TimelineUpdateGoal__score ${
            teamType === Team.AWAY ? 'TimelineUpdateGoal__score--bold' : ''
          }`}
        >
          {awayScore}
        </span>
      </div>
    ) : null;
  }, [teamType, content]);

  const heading = useMemo(
    () => (
      <h1 className="TimelineUpdateGoal__title">
        <span>{title}</span>
        {teamType && teamType !== Team.UNDEFINED && teams && teams[teamType] && (
          <span>{teams[teamType].name}</span>
        )}
      </h1>
    ),
    [title, teams, teamType]
  );

  const scorerName = useMemo(
    () =>
      isScoreContent(content) && 'scorer' in content && content.scorer ? (
        <p className="TimelineUpdateGoal__scorer">{content.scorer}</p>
      ) : null,
    [content]
  );

  const formattedComment = useMemo(
    () =>
      comment && (
        <p
          className="TimelineUpdateGoal__comment Comment"
          dangerouslySetInnerHTML={{__html: comment}}
        />
      ),
    [comment]
  );

  return (
    <article
      className={`TimelineUpdateGoal TimelineUpdateGoal--${teamType} ${
        isOwn ? 'TimelineUpdateGoal--own' : ''
      }`}
    >
      <span className="TimelineUpdateGoal__time">
        {minute}{stoppageTime ? `+${stoppageTime}` : ''}&apos;
      </span>
      <div className="TimelineUpdateGoal__bullet" />
      <img className="TimelineUpdateGoal__icon" src={icon_goal} alt="Γκολ" />
      <div className="TimelineUpdateGoal__content">
        {scorePanel}
        {heading}
        {scorerName}
      </div>
      {formattedComment}
    </article>
  );
};

/**
 * STATUS UPDATE
 */

interface StatusProps extends Props {
  sport: Sport;
  teams: {home: TeamInfo; away: TeamInfo};
}

export const TimelineUpdateStatus: FC<StatusProps> = ({
  update: {type, minute, title, content},
  sport,
  teams,
}: StatusProps) => {
  const formattedTitle = useMemo(
    () =>
      type === TimelineUpdateType.INJURY_TIME_SHOWN && content ? `${title}: ${content}'` : title,
    [type, content, title]
  );

  const statusScore = useMemo(() => {
    if (
      [
        TimelineUpdateType.MATCH_ENDED,
        TimelineUpdateType.PERIOD_SCORE,
        TimelineUpdateType.PERIOD_ENDED,
        TimelineUpdateType.BREAK_START,
      ].includes(type) &&
      isStatusContent(content) &&
      hasScoreInStatus(content)
    ) {
      return (
        <div className="TimelineUpdateStatus__scoreContainer">
          <span className="TimelineUpdateStatus__scoreTeam">{teams.home.shortName}</span>
          <span className="TimelineUpdateStatus__scoreResult">
            <span>{content.score.home}</span>-<span>{content.score.away}</span>
          </span>
          <span className="TimelineUpdateStatus__scoreTeam">{teams.away.shortName}</span>
        </div>
      );
    }
    return null;
  }, [content, teams.away.shortName, teams.home.shortName, type]);

  // exclude first period_start as it coincides with match_started
  // FIXME: minute missing from period_start
  if (type === TimelineUpdateType.PERIOD_START && minute === '0') return null;

  return (
    <article
      className={`TimelineUpdateStatus ${
        [TimelineUpdateType.INJURY_TIME_SHOWN, TimelineUpdateType.PERIOD_START].includes(type)
          ? ''
          : 'TimelineUpdateStatus--highlight'
      }`}
    >
      <div className="TimelineUpdateStatus__header">
        <h1 className="TimelineUpdateStatus__title">{formattedTitle}</h1>
      </div>
      {statusScore}
    </article>
  );
};

/**
 * EDITOR UPDATE
 */

const isEditorSignedContent = (c: any): c is EditorSignedContent =>
  c && typeof c !== 'string' && 'editorImageUrl' in c;

const isEditorPhotoContent = (c: any): c is EditorPhotoContent =>
  c && typeof c !== 'string' && 'photoUrl' in c;

const isEditorEmbedContent = (c: any): c is EditorEmbedContent =>
  c && typeof c !== 'string' && 'embed' in c;

export const TimelineUpdateEditor: FC<Props> = ({update}: Props) => {
  const {type, title, content, comment, isPinned} = update;

  const [scripts, setScripts] = useState<HTMLScriptElement[]>([]);
  const scriptContainerRef = useRef<HTMLParagraphElement>(null);
  const optaContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (scriptContainerRef.current && scripts.length) {
      const container = scriptContainerRef.current;
      loadScriptsInSequence(scripts, container);

      return () => {
        if (container) {
          scripts.forEach(scr => container && container.removeChild(scr));
        }
      };
    }
    return undefined;
  }, [scripts, scriptContainerRef]);


  const formattedTitle = useMemo(
    () =>
      ![
        TimelineUpdateType.PHOTO_HIGHLIGHT,
        TimelineUpdateType.VIDEO_HIGHLIGHT,
        TimelineUpdateType.SOCIAL,
        TimelineUpdateType.OPTA_DATA,
      ].includes(type) && title
      ,
    [type, title]
  );

  const pinnedFormattedTitle = useMemo(
    () => 
    isPinned && ![TimelineUpdateType.SOCIAL,
      TimelineUpdateType.OPTA_DATA,
    ].includes(type) && title
    ,
    [type, title, isPinned]
  )

  const media = useMemo(() => {
    if (
      [TimelineUpdateType.PHOTO_HIGHLIGHT, TimelineUpdateType.SPONSORED].includes(type) &&
      isEditorPhotoContent(content)
    ) {
      return (
        content.photoUrl && (
          <div className="TimelineUpdateEditor__media">
            <figure>
              <img
                className="TimelineUpdateEditor__photo"
                src={content.photoUrl}
                alt={content.photoCaption ?? 'Φωτογραφία'}
              />
              {content.photoCaption && (
                <p className="TimelineUpdateEditor__caption">{content.photoCaption}</p>
              )}
            </figure>
          </div>
        )
      );
    }

    if (
      [
        TimelineUpdateType.SOCIAL,
        TimelineUpdateType.VIDEO_HIGHLIGHT,
        TimelineUpdateType.SPONSORED,
      ].includes(type) &&
      isEditorEmbedContent(content)
    ) {
      // handle scripts from social embeds
      let html = content.embed;
      const scriptMatches = Array.from(html.matchAll(/<script.*?>.*?<\/script>/gis));

      const newScripts: HTMLScriptElement[] = [];

      scriptMatches.forEach(([script]) => {
        if (!script) return;
        // if (script.toLowerCase().includes("connatix")){
        //   html = html.replace(/<script.*?>(.*?)<\/script>/gis, '$1');
        // } else 
        html = script.toLowerCase().includes("connatix") ? html : html.replace(script, '');

        const srcId = script.match(/<script\s+[^>]*id=["'](?<id>[^"']+)["'][^>]*>/i);      
        const srcMatch = script.match(/<script.*?src="(?<src>.*?)".*?>.*?<\/script>/i);
        const contentMatch = script.match(/<script.*?>(?<content>.*?)<\/script>/is);

        if (!srcMatch && !contentMatch) return;

        const scriptEl = document.createElement('script');
        if (srcMatch && srcMatch?.groups?.src) {
          scriptEl.src = srcMatch.groups.src;
        } else if (contentMatch && contentMatch?.groups?.content) {
          scriptEl.innerHTML = contentMatch.groups.content;
        }
        if (script.toLowerCase().includes("connatix") && window.cnx && contentMatch && contentMatch?.groups?.content && srcId?.groups?.id) {
          scriptEl.innerHTML = contentMatch.groups.content;
          scriptEl.id= srcId.groups.id;
        }
        newScripts.push(scriptEl);
      });

      if (newScripts.length) setScripts(newScripts);

      return (
        html && (
          <div className="TimelineUpdateEditor__media">
            <div
              className={`TimelineUpdateEditor__embed ${
                [TimelineUpdateType.VIDEO_HIGHLIGHT, TimelineUpdateType.SPONSORED].includes(type)
                  ? 'TimelineUpdateEditor__embed--intrinsic'
                  : ''
              }`}
              dangerouslySetInnerHTML={{__html: html}}
              ref={scriptContainerRef}
            />
          </div>
        )
      );
    }

    if ([TimelineUpdateType.OPTA_DATA].includes(type) && isEditorEmbedContent(content)) {
      return (
        content.embed && (
          <div className="TimelineUpdateEditor__media">
            <div
              className="TimelineUpdateEditor__embed"
              dangerouslySetInnerHTML={{__html: content.embed}}
              ref={optaContainerRef}
            />
          </div>
        )
      );
    }

    if (type === TimelineUpdateType.EXPERT_COMMENT && isEditorSignedContent(content)) {
      return (
        content.editorImageUrl && (
          <img
            className="TimelineUpdateEditor__editorImage"
            src={content.editorImageUrl}
            alt={title}
          />
        )
      );
    }

    return null;
  }, [type, title, content]); // FIXME: if fixed, this causes re-render loops

  const formattedComment = useMemo(
    () =>
      comment && (
        <p
          className="TimelineUpdateEditor__comment Comment"
          dangerouslySetInnerHTML={{__html: comment}}
        />
      ),
    [comment]
  );
  return (
    (!isPinned ? (
      <article className={`TimelineUpdateEditor TimelineUpdateEditor--${type}`}>
        {media}
        {formattedTitle && (
          <div className="TimelineUpdateEditor__header">
            <h1 className="TimelineUpdateEditor__title">{formattedTitle}</h1>
          </div>
        )}
        {formattedComment}
      </article>
    ): (
      <article className={`TimelineUpdateEditor TimelineUpdateEditor--${type} ${`pinned ${pinnedFormattedTitle && "--title"}${formattedComment && "--comment"}`}`}>
        {media}
        {pinnedFormattedTitle && (
          <div className="TimelineUpdateEditor__header">
            <h1 className="TimelineUpdateEditor__title">{pinnedFormattedTitle}</h1>
          </div>
        )}
        {formattedComment}
      </article>
    ))

  );
};
